import {
  ComponentType,
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useRouter } from "next/router";

import { CreditsBalancePopup } from "./CreditsBalancePopup";

type CreditsBalanceContextValue = {
  isOpened: boolean;
  open?: (params: { withAddGenerativeCredits?: boolean }) => void;
  close?: () => void;
};

export const CreditsBalanceContext = createContext<CreditsBalanceContextValue>({
  isOpened: false,
});

export const CreditsBalanceProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const router = useRouter();

  const [isOpened, setIsOpened] = useState(false);
  const [withAddGenerativeCredits, setWithAddGenerativeCredits] = useState(false);

  useEffect(() => {
    setIsOpened(false);
  }, [router.asPath]);

  const close = useCallback(() => {
    setIsOpened(false);
  }, []);

  const open = useCallback((params: { withAddGenerativeCredits?: boolean }) => {
    setIsOpened(true);
    setWithAddGenerativeCredits(params?.withAddGenerativeCredits ?? false);
  }, []);

  return (
    <CreditsBalanceContext.Provider
      value={{
        isOpened,
        close,
        open,
      }}
    >
      {children}

      <CreditsBalancePopup isOpened={isOpened} onClose={close} withAddGenerativeCredits={withAddGenerativeCredits} />
    </CreditsBalanceContext.Provider>
  );
};

export const useCreditsBalance = () => {
  const { isOpened, close, open } = useContext(CreditsBalanceContext);

  return {
    isOpened,
    close,
    open,
  };
};

export const withCreditsBalanceProvider = (Component: ComponentType) => {
  return (props: any) => (
    <CreditsBalanceProvider>
      <Component {...props} />
    </CreditsBalanceProvider>
  );
};
